import { useEffect, useState } from 'react';
import Select from 'react-select'
import ButtonWrap from '../../components/template/ButtonWrap'
import { useNavigate } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowLeft, faArrowRight, faSpinner } from '@fortawesome/free-solid-svg-icons'
import api from '../../components/utils/api';
import MainTitle from '../../components/template/MainTitle';
import SubTitle from '../../components/template/SubTitle';
import PageDescription from '../../components/template/PageDescription';
import { BASE_EXP_VER } from '../ExperimentsPage/trainingOptions';
import Spinner from '../../components/template/Spinner';
import { getRecentModels } from '../../components/utils/recentModels';

const Selector = ({ label, options, value, setValue, placeholder, noOptionsMessage }) => (
    <div>
        <p className='text-xs font-bold text-left text-gray-800 py-2'>{label}</p>
        <Select
            options={options}
            value={value ? { label: value, value: value } : null}
            onChange={(e) => setValue(e.value)}
            placeholder={placeholder}
            noOptionsMessage={() => noOptionsMessage}
        />
    </div>
)

const ModelSelectionPage = () => {
    const [dbOptions, setDbOptions] = useState([])
    const [expOptions, setExpOptions] = useState([])
    const [modelOptions, setModelOptions] = useState([])

    const [dbVersion, setDbVersion] = useState(undefined)
    const [expVersion, setExpVersion] = useState(undefined)
    const [modelType, setModelType] = useState(undefined)
    const [generalModelId, setGeneralModelId] = useState(undefined)
    const [isResolving, setIsResolving] = useState(false)
    // model types of the latest database's base experiment, for quick access
    const [baseModelTypes, setBaseModelTypes] = useState([])
    const [isBaseModelsLoading, setIsBaseModelsLoading] = useState(true)
    // read once on mount: workspaces this browser opened before
    const [recentModels] = useState(getRecentModels)
    const navigate = useNavigate()

    // the dbs endpoint returns versions newest-first
    const latestDbVersion = dbOptions[0]

    const openBaseModel = async (type) => {
        try {
            const res = await api.get(`/api/general-models/resolve?model_name=${latestDbVersion}_${BASE_EXP_VER}_${type}`)
            navigate(`/post-action/models/${res.data.id}`)
        } catch (err) {
            console.error(err)
        }
    }

    useEffect(() => {
        const fetchDbOptions = async () => {
            try {
                const response = await api.get('/api/model-selection/dbs')
                setDbOptions(response.data.dbs)
                // no db version means the base-model fetch below never runs
                if (response.data.dbs.length === 0) setIsBaseModelsLoading(false)
            } catch (err) {
                console.error(err)
                setIsBaseModelsLoading(false)
            }
        }
        fetchDbOptions()
    }, [])

    useEffect(() => {
        const fetchExpOptions = async () => {
            try {
                const response = await api.get(`/api/model-selection/experiments?db_ver=${dbVersion}`)
                setExpOptions(response.data.experiments)
            } catch (err) {
                console.error(err)
            }
        }

        if (dbVersion) {
            setExpVersion(undefined)
            setExpOptions([])
            fetchExpOptions()
        }
    }, [dbVersion])

    useEffect(() => {
        const fetchModelTypeOptions = async () => {
            try {
                const response = await api.get(`/api/model-selection/model-types?db_ver=${dbVersion}&exp_ver=${expVersion}`)
                setModelOptions(response.data.model_types)
            } catch (err) {
                console.error(err)
            }
        }

        if (dbVersion && expVersion) {
            setModelType(undefined)
            setModelOptions([])
            fetchModelTypeOptions()
        }
    }, [dbVersion, expVersion])

    useEffect(() => {
        const fetchBaseModelTypes = async () => {
            try {
                const res = await api.get(`/api/model-selection/model-types?db_ver=${latestDbVersion}&exp_ver=${BASE_EXP_VER}`)
                setBaseModelTypes(res.data.model_types)
            } catch (err) {
                console.error(err)
            } finally {
                setIsBaseModelsLoading(false)
            }
        }
        if (latestDbVersion) fetchBaseModelTypes()
    }, [latestDbVersion])

    useEffect(() => {
        if (!(dbVersion && expVersion && modelType)) {
            // a partial selection must not keep the previous resolution alive;
            // also clear the flag a cancelled in-flight request leaves behind
            setGeneralModelId(undefined)
            setIsResolving(false)
            return
        }
        // ignore responses of outdated requests when the selection changes quickly
        let cancelled = false
        const fetchGeneralModelId = async () => {
            setIsResolving(true)
            try {
                const response = await api.get(`/api/general-models/resolve?model_name=${dbVersion}_${expVersion}_${modelType}`)
                if (!cancelled) setGeneralModelId(response.data.id)
            } catch (err) {
                console.error(err)
            } finally {
                if (!cancelled) setIsResolving(false)
            }
        }
        fetchGeneralModelId()
        return () => { cancelled = true }
    }, [dbVersion, expVersion, modelType])
    return (
        <div className='default-background'>
            <div className="flex justify-between items-center my-5">
                <MainTitle title="Models" />
                <ButtonWrap contents={<FontAwesomeIcon icon={faArrowLeft} />} onClick={() => navigate('/')} />
            </div>
            <div className="mb-4">
                <PageDescription contents={[
                    "On this page, you can open a trained model's workspace to evaluate, convert, deploy, and download it.",
                    "A model is identified by where it was trained — pick the Cochl DB version, the experiment, and the trained model.",
                ]} />
            </div>
            <div className='mt-8 text-left'>
                <SubTitle title="Model search" />
            </div>
            <div className='md:flex md:items-end md:gap-4 my-3'>
                {[
                    { label: "Database", options: dbOptions, value: dbVersion, setValue: setDbVersion, placeholder: "Choose a Cochl DB version", noOptionsMessage: "Please wait..." },
                    { label: "Experiment", options: expOptions, value: expVersion, setValue: setExpVersion, placeholder: "Choose an experiment", noOptionsMessage: dbVersion ? "No experiments available" : "Choose a Cochl DB version first" },
                    { label: "Trained Model", options: modelOptions, value: modelType, setValue: setModelType, placeholder: "Choose a trained model", noOptionsMessage: expVersion ? "No trained models available" : "Choose an experiment first" },
                ].map((e, i) => <div className='md:flex-1' key={i}>
                    <Selector
                        label={e.label}
                        options={e.options.map(e => ({ label: e, value: e }))}
                        value={e.value}
                        setValue={e.setValue}
                        placeholder={e.placeholder}
                        noOptionsMessage={e.noOptionsMessage}
                    />
                </div>)}
                <div className='mt-5 md:mt-0 text-right'>
                    <ButtonWrap contents={<FontAwesomeIcon icon={isResolving ? faSpinner : faArrowRight} spin={isResolving} />} onClick={() => {
                        navigate(`/post-action/models/${generalModelId}`)
                    }} isDisabled={!generalModelId || isResolving} />
                </div>
            </div>
            {recentModels.length > 0 && <div className='mt-10 text-left'>
                <SubTitle title="Quick access — recent models" />
                <div className='my-3 border rounded-lg divide-y'>
                    {recentModels.map(e => <button
                        key={`${e.modelId}-${e.mergeRuleId}`}
                        type='button'
                        onClick={() => navigate(`/post-action/models/${e.modelId}/merge-rules/${e.mergeRuleId}/detail`)}
                        className='w-full flex justify-between items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-50'
                    >
                        <span>{e.modelName} · {e.mergeRuleName}</span>
                        <FontAwesomeIcon icon={faArrowRight} className='text-gray-400' />
                    </button>)}
                </div>
            </div>}
            {(isBaseModelsLoading || baseModelTypes.length > 0) && <div className='mt-10 text-left'>
                <SubTitle title={`Quick access — base models${latestDbVersion ? ` of ${latestDbVersion}` : ''}`} />
                {isBaseModelsLoading
                    ? <div className='my-3'><Spinner /></div>
                    : <div className='my-3 border rounded-lg divide-y'>
                    {baseModelTypes.map(type => <button
                        key={type}
                        type='button'
                        onClick={() => openBaseModel(type)}
                        className='w-full flex justify-between items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-50'
                    >
                        <span>{`${latestDbVersion}_${BASE_EXP_VER}_${type}`}</span>
                        <FontAwesomeIcon icon={faArrowRight} className='text-gray-400' />
                    </button>)}
                    </div>}
            </div>}
        </div>
    )
}

export default ModelSelectionPage
